import {
	Card,
	CardList,
	CardListSkeleton,
	CardSkeleton,
	Container,
	Heading,
	PlaceholderAlbumArtwork,
	Skeleton,
} from '@audio-network/componentlibrary'
import {
	Configure,
	ConfigureProps,
	Index,
	useHits,
	useInfiniteHits,
	useInstantSearch,
	useSortBy,
	useStats,
} from 'react-instantsearch'
import React, { useEffect, useState } from 'react'
import { SORT_OPTIONS, isMostRecentSort } from '@/constants/search'

import { AlbumResultsProps } from './AlbumResults.types'
import { CARD_LIST_THUMBNAIL_MAX_WIDTH } from '@/constants/sizing'
import Image from 'next/image'
import { MusicAppLink } from '@/utils/shouldLinkToMusicApp'
import { clientGetContentfulAlbums } from '@/client'
import { getAlbumRestrictedAttributes } from './restrictedAlbumAttributesHelper'
import { getAlbumUrl } from '@/utils/urls'
import { mapTracksFromAlgolia } from '../TrackResults/TrackResults.utils'
import optimizeContentfulImage from '@/utils/contentful/optimizeContentfulImage'
import { sendAnalytics } from '@audio-network/analytics'
import useAiSearchStore from '@/stores/aiSearchStore'
import { useTranslation } from 'next-i18next'

const AlbumResults = ({
	trackResults,
	nbTrackHits,
}: AlbumResultsProps): JSX.Element => {
	const [albums, setAlbums] = useState<ContentfulAlbum[]>([])
	const { status } = useInstantSearch()
	const { items } = useHits()
	const { aiSearchPending, aiSearchResults, aiSearchHasFailed } =
		useAiSearchStore()
	const { currentRefinement } = useSortBy(SORT_OPTIONS)

	const { t } = useTranslation(['search'])

	useEffect(() => {
		if (status !== 'idle') {
			return
		}

		const shouldUseAiSearchRanking =
			aiSearchResults && !isMostRecentSort(currentRefinement)

		const fetchAlbums = async (): Promise<void> => {
			const contentfulAlbums = await clientGetContentfulAlbums(
				items.map((item) => item.Album.Id)
			)

			if (contentfulAlbums.length === 0) {
				return
			}

			if (shouldUseAiSearchRanking) {
				/**
				 * Order albums by relevancy suggested by AI provider.
				 *
				 * Because there is no correlation currently between AI order (just track ids)
				 * and the album results from Algolia (just album ids)
				 * we must use our track results from Algolia to determine order
				 */

				const mappedTracks = mapTracksFromAlgolia(trackResults, aiSearchResults)

				const orderedAlbums = Array.from(
					new Set(
						mappedTracks.map((track) =>
							contentfulAlbums.find((album) => album.id === track.album.number)
						)
					)
				).filter((item) => item)

				setAlbums(orderedAlbums)

				return
			}

			/**
			 * Contentful appears to be jumbling the order of albums
			 * so we reorder based on initial results from
			 */
			const orderedAlbums = items
				.map((item) =>
					contentfulAlbums.find((album) => album.id === item.Album.Id)
				)
				.filter((item) => item)

			setAlbums(orderedAlbums)
		}

		if (items.length > 0) {
			fetchAlbums()
		}
	}, [aiSearchResults, items, status, trackResults, currentRefinement])

	const aiResultsNoSearchHits =
		!aiSearchHasFailed && aiSearchResults && nbTrackHits === 0

	const areTrackAiResultsOutOfSync =
		aiSearchResults &&
		trackResults.length > 0 &&
		!trackResults.every((trackResult) =>
			aiSearchResults.some(
				(aiSearchResult) => trackResult.Id === aiSearchResult
			)
		)

	const showSkeletons =
		status !== 'idle' ||
		(items.length > 0 && albums.length === 0) ||
		(items.length > 0 &&
			!albums.every((album) =>
				items.some((item) => item.Album.Id === album.id)
			)) ||
		aiSearchPending ||
		areTrackAiResultsOutOfSync

	if (
		!showSkeletons &&
		(!items || items.length === 0 || aiSearchHasFailed || aiResultsNoSearchHits)
	) {
		return null
	}

	const handleAlbumClick = (album: ContentfulAlbum, index: number): void => {
		sendAnalytics({
			noun: 'Album',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				AlbumNo: album.id,
				AlbumTitle: album.title,
				Position: index + 1,
			},
		})
	}

	return (
		<Container maxWidth="large">
			<Heading level={2} size="XS" uppercase>
				{showSkeletons ? <Skeleton width={160} /> : t('top-albums')}
			</Heading>
			{showSkeletons ? (
				<CardListSkeleton scrollable>
					{Array(16)
						.fill(0)
						?.map((_, index) => {
							return (
								<CardSkeleton
									data-testid="skeleton"
									key={`skeleton-${index}`}
									thumbnailHeight={CARD_LIST_THUMBNAIL_MAX_WIDTH}
									thumbnailWidth={CARD_LIST_THUMBNAIL_MAX_WIDTH}
									title
								/>
							)
						})}
				</CardListSkeleton>
			) : (
				<>
					{albums.length > 0 && (
						<CardList scrollable>
							{/* Map through items to persist Algolia order (Contentful changes it) */}
							{albums.map((album, index) => {
								const albumUrl = getAlbumUrl(album)
								return (
									<MusicAppLink href={albumUrl} elseFragment key={album.id}>
										<Card
											title={album.title}
											href={albumUrl}
											data-testid="album-card"
											onClick={() => handleAlbumClick(album, index)}
											thumbnail={
												album.artwork?.url ? (
													<Image
														alt=""
														height={CARD_LIST_THUMBNAIL_MAX_WIDTH}
														src={optimizeContentfulImage(album.artwork?.url, {
															width: CARD_LIST_THUMBNAIL_MAX_WIDTH,
															height: CARD_LIST_THUMBNAIL_MAX_WIDTH,
														})}
														width={CARD_LIST_THUMBNAIL_MAX_WIDTH}
													/>
												) : (
													<PlaceholderAlbumArtwork />
												)
											}
										/>
									</MusicAppLink>
								)
							})}
						</CardList>
					)}
				</>
			)}
		</Container>
	)
}

const ConfiguredAlbumResults = ({
	locale,
}: {
	locale: string
}): JSX.Element => {
	const { indexUiState } = useInstantSearch()
	const { nbHits } = useStats()
	const { items } = useInfiniteHits()

	const sortByMostRecent = !!indexUiState.sortBy

	const configureProps = {
		hitsPerPage: 16,
		restrictSearchableAttributes: getAlbumRestrictedAttributes(locale),
	} as ConfigureProps

	return (
		<Index
			indexName={`${process.env.NEXT_PUBLIC_ALGOLIA_ALBUMS_INDEX}${
				sortByMostRecent ? '_most_recent' : ''
			}`}
		>
			<Configure {...configureProps} />
			<AlbumResults trackResults={items} nbTrackHits={nbHits} />
		</Index>
	)
}

export default ConfiguredAlbumResults
